import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { apiError, formatMoney, formatDate, formatDateTime, QUOTATION_STATUSES, STAFF_ROLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/StatusBadge";
import QuotationForm from "@/components/QuotationForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowLeft, Pencil, Mail, Printer, CheckCircle2, XCircle, History, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function QuotationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isStaff = STAFF_ROLES.includes(user.role);
  const [q, setQ] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [openRevision, setOpenRevision] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/quotations/${id}`);
      setQ(data);
    } catch (e) {
      toast.error(apiError(e));
      navigate("/quotations");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isStaff) api.get("/customers").then(({ data }) => setCustomers(data)).catch(() => {});
  }, [isStaff]);

  const saveRevision = async (payload) => {
    setSaving(true);
    try {
      await api.put(`/quotations/${id}`, payload);
      toast.success("New revision saved");
      setFormOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/quotations/${id}/send-email`);
      toast.success(`Quotation emailed to ${data.to}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    try {
      await api.patch(`/quotations/${id}/status`, { status });
      toast.success(`Quotation marked as ${status}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this quotation? This action is logged in the audit trail.")) return;
    try {
      await api.delete(`/quotations/${id}`);
      toast.success("Quotation deleted");
      navigate("/quotations");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!q) return <div className="py-20 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const editable = isStaff && !["accepted", "rejected"].includes(q.status);
  const canAcceptReject = (user.role === "customer" && q.status === "sent") || (isStaff && q.status === "sent");

  return (
    <div className="space-y-6" data-testid="quotation-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/quotations" className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-quotations-link">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to quotations
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="font-display font-mono text-2xl font-bold tracking-tight sm:text-3xl" data-testid="quotation-number">{q.quotation_no}</h1>
            <span className="rounded-sm border border-border px-2 py-0.5 font-mono text-xs" data-testid="quotation-version-badge">Revision v{q.version}</span>
            <StatusBadge status={q.status} label={QUOTATION_STATUSES[q.status]} testid="quotation-status-badge" />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {q.customer?.company_name} · Created {formatDate(q.created_at)} by {q.created_by}
            {q.valid_until && <> · Valid until {formatDate(q.valid_until)}</>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {editable && (
            <Button variant="outline" size="sm" onClick={() => setFormOpen(true)} data-testid="edit-quotation-button">
              <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit (New Revision)
            </Button>
          )}
          {isStaff && (
            <Button variant="outline" size="sm" onClick={sendEmail} disabled={sending} data-testid="send-email-quotation-button">
              {sending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Mail className="mr-1.5 h-3.5 w-3.5" />}
              Email to Customer
            </Button>
          )}
          <Link to={`/quotations/${id}/print`} target="_blank">
            <Button variant="outline" size="sm" data-testid="print-quotation-link"><Printer className="mr-1.5 h-3.5 w-3.5" /> Print</Button>
          </Link>
          {canAcceptReject && (
            <>
              <Button size="sm" onClick={() => setStatus("accepted")} data-testid="accept-quotation-button">
                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Accept
              </Button>
              <Button size="sm" variant="destructive" onClick={() => setStatus("rejected")} data-testid="reject-quotation-button">
                <XCircle className="mr-1.5 h-3.5 w-3.5" /> Reject
              </Button>
            </>
          )}
          {isStaff && ["draft", "rejected"].includes(q.status) && (
            <Button size="sm" variant="ghost" onClick={remove} data-testid="delete-quotation-button"><Trash2 className="h-4 w-4 text-red-500" /></Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2" data-testid="quotation-items-card">
          <CardHeader><CardTitle className="text-base">Line Items</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Service</TableHead>
                  <TableHead className="w-16 text-center">Qty</TableHead>
                  <TableHead className="w-28 text-right">Unit Price</TableHead>
                  <TableHead className="w-28 text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.items.map((item, i) => (
                  <TableRow key={i} data-testid={`quotation-detail-item-${i}`}>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-center font-mono">{item.quantity}</TableCell>
                    <TableCell className="text-right font-mono">{formatMoney(item.unit_price, q.currency)}</TableCell>
                    <TableCell className="text-right font-mono">{formatMoney(item.quantity * item.unit_price, q.currency)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="ml-auto mt-4 w-64 space-y-1 font-mono text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{formatMoney(q.subtotal, q.currency)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Discount ({q.discount_percent}%)</span><span>-{formatMoney(q.discount_amount, q.currency)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Tax ({q.tax_percent}%)</span><span>{formatMoney(q.tax_amount, q.currency)}</span></div>
              <div className="flex justify-between border-t border-border pt-1.5 text-base font-semibold">
                <span>Total</span><span data-testid="quotation-total">{formatMoney(q.total, q.currency)}</span>
              </div>
            </div>
            {q.notes && <p className="mt-4 border-t border-border pt-3 text-sm text-muted-foreground"><span className="font-medium text-foreground">Terms: </span>{q.notes}</p>}
          </CardContent>
        </Card>

        <div className="space-y-6">
          <Card data-testid="revision-history-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base"><History className="h-4 w-4 text-primary" /> Revision History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="relative space-y-0 border-l border-border pl-5">
                <div className="relative pb-5">
                  <span className="absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full bg-primary" />
                  <div className="font-mono text-xs font-semibold" data-testid="revision-current">v{q.version} — Current</div>
                  <div className="text-xs text-muted-foreground">{formatDateTime(q.updated_at)}</div>
                </div>
                {[...(q.revisions || [])].reverse().map((rev) => (
                  <div key={rev.version} className="relative pb-5">
                    <span className="absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full border border-border bg-muted" />
                    <button
                      className="text-left font-mono text-xs font-medium text-muted-foreground hover:text-foreground"
                      onClick={() => setOpenRevision(openRevision === rev.version ? null : rev.version)}
                      data-testid={`revision-toggle-v${rev.version}`}
                    >
                      v{rev.version} — superseded {openRevision === rev.version ? "▲" : "▼"}
                    </button>
                    <div className="text-xs text-muted-foreground">{formatDateTime(rev.changed_at)} · {rev.changed_by}</div>
                    {rev.note && <div className="mt-0.5 text-xs italic text-muted-foreground">"{rev.note}"</div>}
                    {openRevision === rev.version && (
                      <div className="mt-2 rounded-md border border-border bg-muted/40 p-3 font-mono text-[11px] leading-relaxed" data-testid={`revision-snapshot-v${rev.version}`}>
                        <div className="mb-1 text-muted-foreground">Total: {formatMoney(rev.snapshot.total, rev.snapshot.currency)} · Tax {rev.snapshot.tax_percent}% · Disc {rev.snapshot.discount_percent}%</div>
                        {rev.snapshot.items.map((it, i) => (
                          <div key={i} className="truncate">• {it.description} × {it.quantity} @ {formatMoney(it.unit_price, rev.snapshot.currency)}</div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {(q.revisions || []).length === 0 && <p className="pb-1 text-xs text-muted-foreground">No previous revisions. Edits create a new version.</p>}
              </div>
            </CardContent>
          </Card>

          {isStaff && (q.email_log || []).length > 0 && (
            <Card data-testid="email-log-card">
              <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Mail className="h-4 w-4 text-primary" /> Email Log</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {q.email_log.map((e, i) => (
                  <div key={i} className="rounded-md border border-border px-3 py-2 text-xs" data-testid={`email-log-entry-${i}`}>
                    <div className="font-medium">Sent to {e.to} (v{e.version})</div>
                    <div className="text-muted-foreground">{formatDateTime(e.sent_at)} · by {e.sent_by}</div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <QuotationForm
        open={formOpen}
        onOpenChange={setFormOpen}
        customers={customers}
        initial={q}
        saving={saving}
        onSubmit={saveRevision}
        title={`Edit ${q.quotation_no} (creates v${q.version + 1})`}
      />
    </div>
  );
}
